import {
  Add,
  BookmarkRounded,
  EventRounded,
  LocalActivityRounded,
  PeopleAltRounded,
  SchoolRounded,
  ViewCarouselRounded
} from '@mui/icons-material'
import { Box, ListItemIcon, MenuItem, Toolbar, Tooltip, Typography } from '@mui/material'
import { ThemeProvider as ThemeProviderV5 } from '@mui/material/styles'
import { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, useNavigate } from 'react-router'
import logo from '../../../../assets/Images/logos/logolight.svg'
import { toggleFormCourse } from '../../../../redux/actions/courses'
import { toggleFormEvent } from '../../../../redux/actions/events'
import { toggleFormOffer } from '../../../../redux/actions/offers'
import { toggleFormCreateResources } from '../../../../redux/actions/resources'
import { toggleFormSlider } from '../../../../redux/actions/sliders'
import { toggleFormUsers } from '../../../../redux/actions/users'
import { IRootState } from '../../../../redux/types'
import useScreenWidth from '../../hooks/useScreenWidth'
import HiddenMobile from '../../styles/HiddenMobile'
import { ContainerActions, DropdownActions, GlobalButtonAdd } from '../../styles/layouts/AdminLayout'
import { LinkAppLogo, MainContent, NavLogo, StyledAppBar, StyledToolbar } from '../../styles/layouts/MainLayout'
import { adminThemeV5 } from '../../styles/themes/AdminTheme'
import AdminMobileMenu from '../AdminMobileMenu'
import CustomSnackBar from '../CustomSnackBar'
import BrandMobileFilter from '../filters/BrandMobileFilter'
import LanguageFilter from '../filters/LanguageFilter'
import AccountMenu from './AccountMenu'
import Footer from './Footer'
import Sidebar from './navigation/Sidebar'
import '../../styles/sass/messagebird.scss'
import { ROLES } from '../../../backoffice/users/models/user'
import { isManager } from '../../../backoffice/users/utils/RolesHelper'

function AdminLayout() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const isTablet = useScreenWidth(920)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { user: authUser, error: errorAuth, msg: msgAuth } = useSelector((state: IRootState) => state.auth)

  const [anchorAdd, setAnchorAdd] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (localStorage.getItem('accept_terms') === 'true' && authUser !== null && authUser.me && authUser.verification === 1)
      navigate('/terms')
  }, [])

  const handleAddClose = () => {
    setAnchorAdd(null)
  }

  const handleClickAdd = (event: MouseEvent<HTMLElement>) => {
    setAnchorAdd(event.currentTarget)
  }

  if (localStorage.getItem('login_redirect')) {
    let tmpPath = localStorage.getItem('login_redirect')
    localStorage.removeItem('login_redirect')
    window.location.href = tmpPath || '/inicio'
  }

  if (authUser.role_id === 6 || authUser.role_id === 7) {
    return <Navigate to="/inicio" replace />
  }

  return (
    <ThemeProviderV5 theme={adminThemeV5}>
      <Box display="flex" id="admin-main-container">
        <StyledAppBar position="fixed" className="admin">
          <Toolbar>
            <LinkAppLogo href="/escritorio">
              <NavLogo src={String(logo)} alt={`Logo de ${import.meta.env.VITE_NAME}`} />
            </LinkAppLogo>

            <HiddenMobile>
              <Typography sx={{ color: 'white', letterSpacing: '0.5px' }}>
                {authUser.role_id === 1 && t('common.admin').toLocaleUpperCase()}
                {authUser.role_id === 2 && t('common.admin').toLocaleUpperCase()}
                {authUser.role_id === 3 && t('supplier').toLocaleUpperCase()}
                {authUser.role_id === 4 && t('commercial').toLocaleUpperCase()}
                {authUser.role_id === 5 && t('trainer').toLocaleUpperCase()}
              </Typography>
            </HiddenMobile>

            <BrandMobileFilter theme="admin" type="nav" />

            {!isMobile && (
              <>
                <div style={{ flexGrow: 1 }} />
                {(isManager(authUser.role_id) || authUser.role_id === ROLES.trainingManager) && (
                  <Tooltip title={`${t('add')}`}>
                    <GlobalButtonAdd
                      aria-controls="add-menu"
                      aria-haspopup="true"
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={handleClickAdd}
                    >
                      <Add htmlColor="white" />
                    </GlobalButtonAdd>
                  </Tooltip>
                )}
                <LanguageFilter theme="admin" />
              </>
            )}
            {isMobile && (
              <>
                <LanguageFilter theme="admin" />
                <div style={{ flexGrow: 1 }} />
              </>
            )}
            <AccountMenu theme="admin" />
          </Toolbar>
        </StyledAppBar>

        <Sidebar theme="admin" />

        <MainContent style={{ padding: '0px' }}>
          <StyledToolbar />
          <Outlet />
          <Footer />
          <AdminMobileMenu action={handleClickAdd} />
        </MainContent>
      </Box>

      <DropdownActions
        variant="temporary"
        anchor={isTablet ? 'bottom' : 'right'}
        ModalProps={{ keepMounted: true }}
        open={Boolean(anchorAdd)}
        onClose={handleAddClose}
      >
        <ContainerActions>
          <MenuItem
            data-testid={'menu-usuarios'}
            onClick={() => {
              dispatch(toggleFormUsers())
              navigate('/admin/usuarios')
              handleAddClose()
            }}
          >
            <ListItemIcon>
              <PeopleAltRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">{t('user')}</Typography>
          </MenuItem>
          <MenuItem
            data-testid={'menu-biblioteca'}
            onClick={() => {
              dispatch(toggleFormCreateResources())
              navigate('/admin/biblioteca')
              handleAddClose()
            }}
          >
            <ListItemIcon>
              <BookmarkRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">{t('document')}</Typography>
          </MenuItem>
          <MenuItem
            data-testid={'menu-cursos'}
            onClick={() => {
              dispatch(toggleFormCourse())
              navigate('/admin/cursos')
              handleAddClose()
            }}
          >
            <ListItemIcon>
              <SchoolRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">{t('course')}</Typography>
          </MenuItem>
          <MenuItem
            data-testid={'menu-eventos'}
            onClick={() => {
              dispatch(toggleFormEvent())
              navigate('/admin/events')
              handleAddClose()
            }}
          >
            <ListItemIcon>
              <EventRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">{t('event')}</Typography>
          </MenuItem>
          <MenuItem
            data-testid={'menu-sliders'}
            onClick={() => {
              dispatch(toggleFormSlider())
              navigate('/admin/sliders')
              handleAddClose()
            }}
          >
            <ListItemIcon>
              <ViewCarouselRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Slider</Typography>
          </MenuItem>
          <MenuItem
            data-testid={'menu-ofertas'}
            onClick={() => {
              dispatch(toggleFormOffer())
              navigate('/admin/ofertas')
              handleAddClose()
            }}
          >
            <ListItemIcon>
              <LocalActivityRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">{t('offer')}</Typography>
          </MenuItem>
          {isTablet && (
            <MenuItem onClick={handleAddClose}>
              <Typography variant="inherit">{t('back')}</Typography>
            </MenuItem>
          )}
        </ContainerActions>
      </DropdownActions>

      <CustomSnackBar errors={[errorAuth]} msgs={[msgAuth]} />
    </ThemeProviderV5>
  )
}

export default AdminLayout
